import * as satellite from "satellite.js"

const digits = /\d{1,6}/g // Matches any sequence of digits like NORAD ID

/**
 * a utility function to, given a block of plaintext,
 *  return an Array of string NORAD IDs that has been detected in the block
 * of text. This is useful for extracting lists from things like user input,
 * especially when a user copies and pastes input from a randome source or
 * text message.
 *
 * @param text String
 * @returns {*[]} Array<NoradID>
 */
export const parseNORADIDs = (text) => {
  if (!text) {
    return []
  }
  return [...text.matchAll(digits)].map((m) => m[0])
}

/**
 * Checks if a TLE is well-formed
 *
 * @param tle the TLE as a string with lines seperated by \n
 * @returns {boolean}
 */
export const validateTLE = (tle) => {
  // Split the TLE into lines
  const tleLines = tle.trim().split("\n")

  if (tleLines.length > 3) {
    return false
  }

  // if there are 3 lines drop the first
  if (tleLines.length === 3) {
    tleLines.shift()
  }

  // Check if there are exactly 2 lines
  if (tleLines.length !== 2) {
    return false
  }

  const [line1, line2] = tleLines

  try {
    // Use satellite.js to parse the TLE
    const satrec = satellite.twoline2satrec(line1, line2)

    // Check if the satrec object has an error
    if (satrec.error) {
      return false
    }

    // Additional checks for NaN values in key satrec properties
    const keysToCheck = [
      "epochyr",
      "epochdays",
      "ndot",
      "nddot",
      "bstar",
      "inclo",
      "nodeo",
      "ecco",
      "argpo",
      "mo",
      "no",
    ]
    for (const key of keysToCheck) {
      if (isNaN(satrec[key])) {
        return false
      }
    }

    // Check if the satellite number matches in both lines
    const satNumberLine1 = line1.slice(2, 7).trim()
    const satNumberLine2 = line2.slice(2, 7).trim()
    if (satNumberLine1 !== satNumberLine2) {
      return false
    }

    // Check checksum for both lines
    if (!checkTLEChecksum(line1) || !checkTLEChecksum(line2)) {
      return false
    }

    return true
  } catch (error) {
    // If an error occurs, the TLE is not valid
    return false
  }
}

// Function to check the checksum of a TLE line
export const checkTLEChecksum = (line) => {
  const checksum = parseInt(line.slice(-1), 10)
  let computedSum = 0

  for (let i = 0; i < 68; i++) {
    const char = line.charAt(i)
    if (char >= "0" && char <= "9") {
      computedSum += parseInt(char, 10)
    } else if (char === "-") {
      computedSum += 1
    }
  }

  return computedSum % 10 === checksum
}

/**
 * Simple service to update the name in line 0 of a TLE
 *
 * @param newName the new name to set
 * @param tle the tle as a string with lines seperated by \n
 * @returns {string} the modified tle
 */
export const updateTLEObjectName = (newName = "", tle = "") => {
  // Split the TLE into lines
  const tleLines = tle.trim().split("\n")
  // check the input
  if (tleLines.length < 2 || tleLines > 3) {
    throw new Error("invalid input: tle is not an array of 2 or 3 strings")
  }
  if (!newName) {
    throw new Error("invalid input: newName is empty")
  }
  const line0 = `0 ${newName.trim()}`
  if (tleLines.length === 3) {
    tleLines[0] = line0
  } else if (tleLines.length === 2) {
    tleLines.unshift(line0)
  }
  return tleLines.join("\n")
}

// get the epoch of the TLE
export const getEpoch = (tle) => {
  try {
    let line1 = ""
    let line2 = ""
    if (tle.length === 2) {
      line1 = tle[0]
      line2 = tle[1]
    } else {
      line1 = tle[1]
      line2 = tle[2]
    }
    const satrec = satellite.twoline2satrec(line1, line2)
    return yearFractionalDaysToDate(satrec.epochyr, satrec.epochdays)
  } catch (error) {
    return null
  }
}

const yearFractionalDaysToDate = (epochyr, epochdays) => {
  // Create a Date object starting at the beginning of the epoch year
  const startOfYear = new Date(Date.UTC(2000 + epochyr, 0, 1)) // Jan 1st, 00:00:00 of the epoch year
  // Add the epoch days (subtract 1 because Jan 1st is day 1, not day 0)
  const epochMilliseconds = (epochdays - 1) * 24 * 60 * 60 * 1000
  return new Date(startOfYear.getTime() + epochMilliseconds).toISOString()
}
